<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        Top bookers
      </h2>
    </div>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <template v-else-if="topBookers.length > 0">
        <table class="panel-table">
          <thead>
            <tr>
              <th>User</th>
              <th class="text-right">
                Bookings
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(booker, index) in visibleBookers"
              :key="booker.userId || index"
              :class="{ 'clickable': booker.account_id }"
              @click="booker.account_id && handleBookerClickGuarded(booker)"
            >
              <td>
                <div class="user-cell">
                  <div class="user-image">
                    <img
                      v-if="booker.avatar"
                      :src="booker.avatar"
                      :alt="booker.name"
                    >
                    <div
                      v-else
                      class="image-placeholder"
                    >
                      {{ getInitials(booker.name) }}
                    </div>
                  </div>
                  <span class="user-name">{{ booker.name }}</span>
                </div>
              </td>
              <td class="text-right">
                {{ booker.count }}
              </td>
            </tr>
          </tbody>
        </table>
        <button
          v-if="hasMore"
          class="load-more-btn"
          @click="loadMore"
        >
          Load more
        </button>
      </template>
      <div
        v-else
        class="panel-empty"
      >
        No bookings found
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted } from 'vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { getDateRangeTimestamps } from '../utils/dateRanges.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

defineEmits(['close'])

const dateRange = 'last_30_days'
const referenceDate = useReferenceDate()
const timestamps = computed(() => getDateRangeTimestamps(dateRange, referenceDate.value))
const locationIds = useLocationFilter()

const topBookersQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_to: $to_timestamp
      end_timestamp_from: $from_timestamp
      limit: $limit
      page: $page
      type: BOOKING
    ) {
      total
      data {
        start_timestamp
        owner_account {
          account_id
          name
          avatar {
            url
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

const scheduleVariables = computed(() => {
  const now = Math.floor(Date.now() / 1000)
  return {
    from_timestamp: now - (180 * 24 * 60 * 60),
    to_timestamp: now,
    limit: 1000,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  }
})

const { data, loading, error, cleanup } = useGraphQLData({
  dataKey: 'top_bookers_view_all',
  query: topBookersQuery,
  variables: scheduleVariables,
  priority: 1,
  autoFetch: true
})

const topBookers = computed(() => {
  if (!data.value?.schedule?.data) return []

  const allItems = data.value.schedule.data
  const fromTimestamp = timestamps.value.from_timestamp
  const toTimestamp = timestamps.value.to_timestamp

  const bookingItems = allItems.filter(item => {
    if (!item.owner_account || !item.start_timestamp) return false
    return item.start_timestamp >= fromTimestamp && item.start_timestamp <= toTimestamp
  })

  const userMap = new Map()
  bookingItems.forEach(item => {
    const account = item.owner_account
    if (!account) return
    const accountName = account.name || '(Unnamed)'
    const accountId = account.account_id || null
    const userId = accountId ? String(accountId) : accountName
    const existing = userMap.get(userId) || {
      userId,
      account_id: accountId,
      name: accountName,
      avatar: account.avatar?.url || null,
      count: 0
    }
    userMap.set(userId, { ...existing, count: existing.count + 1 })
  })

  return Array.from(userMap.values()).sort((a, b) => b.count - a.count)
})

const PAGE_SIZE = 20
const visibleCount = ref(PAGE_SIZE)
const visibleBookers = computed(() => topBookers.value.slice(0, visibleCount.value))
const hasMore = computed(() => visibleCount.value < topBookers.value.length)
const loadMore = () => { visibleCount.value += PAGE_SIZE }

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) { /* ignore */ }
  }
  return { client_id: clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const handleBookerClickGuarded = guard((booker) => {
  const accountId = booker.account_id
  if (!accountId || (typeof accountId === 'string' && !/^\d+$/.test(accountId))) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(accountId),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.panel-table {
  width: 100%;
  border-collapse: collapse;
}

.panel-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.panel-table th.text-right {
  text-align: right;
  width: 80px;
}

.panel-table tbody tr {
  height: 48px;
}

.panel-table td {
  padding: 0 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-table td.text-right {
  text-align: right;
  font-weight: 600;
}

.panel-table tbody tr:last-child td {
  border-bottom: none;
}

.panel-table tbody tr.clickable {
  cursor: pointer;
}

.panel-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.user-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.user-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.load-more-btn {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: white;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.load-more-btn:hover {
  background: #f3f4f6;
}

.user-name {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
</style>
